import React, { useState } from 'react';
import {
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import PageHeader from '../../components/account/PageHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import {
  otherChannels,
  primaryChannels,
  searchHelp,
  SupportChannel,
} from '../../data/account';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * The help desk.
 *
 * Quick answers first, then a way to reach a person. That order is deliberate:
 * four of the five reasons anyone opens this page are answerable without
 * anyone's time, and putting the chat button at the top guarantees a queue of
 * questions the page already answers.
 *
 * ---------------------------------------------------------------------------
 * WHAT ACTUALLY WORKS
 * ---------------------------------------------------------------------------
 * The search, the topics and their answers are real and local. The outward
 * channels hand off to the OS - `tel:`, `mailto:` and a WhatsApp link, opened
 * with `Linking` - so those genuinely leave the app. "Start Chat" opens the
 * app's own Chat tab, which has no transport behind it yet and says so on its
 * own screen.
 */

type Props = {
  onBack: () => void;
  /** Opens the Chat tab, for the one channel that lives inside the app. */
  onStartChat: () => void;
  /** How many requests are on file, for the row that opens them. */
  openTickets?: number;
  onOpenTickets?: () => void;
  onNewTicket?: () => void;
};

function HelpSupportScreen({
  onBack,
  onStartChat,
  openTickets = 0,
  onOpenTickets,
  onNewTicket,
}: Props) {
  const resp = useResponsive();
  const [query, setQuery] = useState('');
  /** Which answer is expanded, or null. One at a time. */
  const [open, setOpen] = useState<string | null>(null);

  const topics = searchHelp(query);

  /*
   * Hands a channel to the OS, or to the Chat tab.
   *
   * `openURL` rejects when nothing can handle the scheme - no dialler on a
   * tablet, no mail account, WhatsApp not installed. Swallowed rather than
   * surfaced: the number and the address are printed on the row, so there is
   * always a way through even when the tap does nothing.
   */
  const openChannel = (channel: SupportChannel) => {
    if (!channel.url) {
      onStartChat();
      return;
    }
    Linking.openURL(channel.url).catch(() => {});
  };

  return (
    <View style={styles.root}>
      <PageHeader title="Help & Support" onBack={onBack} />

      {/*
        `flex: 1` on the scroller itself, not only on its content.

        Inside a flex column a ScrollView with no flex of its own sizes to its
        content and overflows the parent, so the foot of a long list is simply
        cut off - worst on whichever filter shows the most. Flexed, it takes the
        space left under the header and scrolls inside it.
      */}
      <ScrollView
        style={styles.scroller}
        /* Zero on a phone; on a tablet it holds the page to a readable
           column and centres it. See `theme/responsive.ts`. */
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <Text variant="title" style={styles.lede}>
          How can we help you?
        </Text>

        <View style={styles.search}>
          <Icon name="search" size={18} color={colors.inkMuted} />
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Search for help"
            placeholderTextColor={colors.inkSubtle}
            style={styles.searchInput}
            accessibilityLabel="Search for help"
            returnKeyType="search"
          />
          {query ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              onPress={() => setQuery('')}
              hitSlop={10}
            >
              {/* The icon set has no cross; a rotated plus is the same shape. */}
              <Icon
                name="plus"
                size={17}
                color={colors.inkMuted}
                style={styles.clear}
              />
            </Pressable>
          ) : null}
        </View>

        {/* ------------------------------------------------------ quick help */}
        <Text variant="labelSm" color={colors.inkMuted}>
          Quick Help
        </Text>

        <View style={styles.card}>
          {topics.length === 0 ? (
            <View style={styles.noMatch}>
              <Text variant="bodySm" color={colors.inkMuted}>
                Nothing matched “{query}”. Try the channels below.
              </Text>
            </View>
          ) : (
            topics.map((topic, i) => {
              const expanded = open === topic.id;
              return (
                <View key={topic.id} style={i > 0 ? styles.divided : null}>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityState={{ expanded }}
                    accessibilityLabel={topic.question}
                    onPress={() => setOpen(expanded ? null : topic.id)}
                    style={({ pressed }) => [
                      styles.topic,
                      pressed ? styles.pressed : null,
                    ]}
                  >
                    <Icon name={topic.icon} size={17} color={colors.primary} />
                    <Text variant="bodySm" style={styles.grow}>
                      {topic.question}
                    </Text>
                    <Icon
                      name={expanded ? 'chevronDown' : 'chevronRight'}
                      size={15}
                      color={colors.borderStrong}
                    />
                  </Pressable>

                  {expanded ? (
                    <Text variant="caption" color={colors.inkMuted} style={styles.answer}>
                      {topic.answer}
                    </Text>
                  ) : null}
                </View>
              );
            })
          )}
        </View>

        {/*
          ---------------------------------------------------- your requests
          Above the channels, below the answers.

          Somebody who has already asked us something is not here to read the
          FAQ or to start a second conversation about it - they are here to
          find out what happened to the first. That makes this row the most
          likely reason to open this page a second time, and it belongs ahead
          of every way to start again.
        */}
        {onOpenTickets ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`My requests. ${openTickets} on file`}
            onPress={onOpenTickets}
            style={({ pressed }) => [
              styles.card,
              styles.requests,
              pressed ? styles.pressed : null,
            ]}
          >
            <View style={styles.requestsMark}>
              <Icon name="headset" size={19} color={colors.primary} />
            </View>
            <View style={styles.grow}>
              <Text variant="subtitle">My requests</Text>
              <Text variant="caption" color={colors.inkMuted}>
                {openTickets > 0
                  ? `${openTickets} request${openTickets === 1 ? '' : 's'} on file`
                  : 'Anything you have raised with us'}
              </Text>
            </View>
            <Icon name="chevronRight" size={15} color={colors.borderStrong} />
          </Pressable>
        ) : null}

        {onNewTicket ? (
          <Button
            label="Raise a request"
            variant="secondary"
            leadingIcon="plus"
            onPress={onNewTicket}
          />
        ) : null}

        {/* --------------------------------------------------- the two cards */}
        <View style={styles.pair}>
          {primaryChannels().map(channel => (
            <Pressable
              key={channel.id}
              accessibilityRole="button"
              accessibilityLabel={`${channel.label}. ${channel.detail}`}
              onPress={() => openChannel(channel)}
              style={({ pressed }) => [
                styles.channel,
                pressed ? styles.pressed : null,
              ]}
            >
              <Icon name={channel.icon} size={21} color={colors.primary} />
              <Text variant="subtitle" align="center">
                {channel.label}
              </Text>
              <Text variant="caption" align="center" color={colors.inkMuted}>
                {channel.detail}
              </Text>
              <Text variant="labelSm" color={colors.primary}>
                {channel.action}
              </Text>
            </Pressable>
          ))}
        </View>

        {/* ------------------------------------------------------ the others */}
        <Text variant="labelSm" color={colors.inkMuted}>
          Other Options
        </Text>

        <View style={styles.card}>
          {otherChannels().map((channel, i) => (
            <Pressable
              key={channel.id}
              accessibilityRole="button"
              accessibilityLabel={`${channel.label}, ${channel.detail}`}
              onPress={() => openChannel(channel)}
              style={({ pressed }) => [
                styles.topic,
                i > 0 ? styles.divided : null,
                pressed ? styles.pressed : null,
              ]}
            >
              <Icon name={channel.icon} size={17} color={colors.primary} />
              <View style={styles.grow}>
                <Text variant="bodySm">{channel.label}</Text>
                <Text variant="caption" color={colors.inkMuted}>
                  {channel.detail}
                </Text>
              </View>
              <Icon name="chevronRight" size={15} color={colors.borderStrong} />
            </Pressable>
          ))}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  /* A row rather than one of the paired cards: it is a destination with a
     count, not a channel to start something new in. */
  requests: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
  },
  requestsMark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.md,
    // Clears the floating tab bar, which overlays the bottom of the screen.
    paddingBottom: 120,
  },
  lede: {
    fontWeight: '700',
  },

  search: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    height: 48,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  searchInput: {
    flex: 1,
    ...inputText,
  },
  /* The icon set has no cross; a rotated plus is the same shape. */
  clear: {
    transform: [{ rotate: '45deg' }],
  },

  card: {
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  /* On the child rather than the card, so the first row has no rule above it. */
  divided: {
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  topic: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
  },
  answer: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
    // Lines up under the question, clear of its icon.
    paddingLeft: spacing.md + 17 + spacing.md,
  },
  noMatch: {
    padding: spacing.md,
  },
  grow: {
    flex: 1,
  },

  pair: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  channel: {
    flex: 1,
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default HelpSupportScreen;
